import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, ClipboardList, NotebookPen, CalendarClock, BookOpen,
  MessageCircle, LifeBuoy, Target, CheckCircle2,
} from "lucide-react";
import { professionalLabel } from "@/lib/professional";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function PatientHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/me/summary").then((r) => setS(r.data)); }, []);

  const first = user?.name?.split(" ")[0];
  const na = s?.next_action || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-up">
      <div>
        <p className="text-sm text-[#94A3B8]">Olá, {first} 👋</p>
        <h1 className="font-heading text-3xl font-extrabold text-[#1F3B4D]">O que eu preciso fazer agora?</h1>
      </div>

      {/* Minha próxima ação — elemento mais importante */}
      <Card data-testid="next-action-card" className="rounded-2xl border-0 overflow-hidden text-white shadow-lg"
        style={{ background: "linear-gradient(120deg,#0F766E 0%,#0d6259 55%,#1F3B4D 100%)" }}>
        <div className="p-6 sm:p-8 relative">
          <div className="absolute right-6 top-6 opacity-20"><Target size={90} /></div>
          <div className="flex items-center gap-2 text-[#34D399] text-xs font-semibold uppercase tracking-wider">
            <Target size={15} /> Minha próxima ação
          </div>
          {na.title ? (
            <>
              <h2 className="font-heading text-2xl font-bold mt-3 max-w-xl">{na.title}</h2>
              {na.description && <p className="text-white/80 mt-2 max-w-xl">{na.description}</p>}
              <Button onClick={() => nav("/registro")} data-testid="start-next-action-button"
                className="mt-5 rounded-full bg-white text-[#0F766E] hover:bg-white/90 gap-2 font-semibold">
                Registrar agora <ArrowRight size={16} />
              </Button>
            </>
          ) : (
            <p className="text-white/80 mt-3">Nenhuma ação definida no momento. Seu profissional atualizará em breve.</p>
          )}
        </div>
      </Card>

      {/* O que tenho para fazer */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-[#1F3B4D] flex items-center gap-2">
            <ClipboardList size={18} className="text-[#0F766E]" /> O que tenho para fazer
          </h3>
          <button onClick={() => nav("/atividades")} className="text-sm text-[#0F766E] hover:underline" data-testid="see-all-activities">Ver todas</button>
        </div>
        {s && s.pending_activities.length === 0 && (
          <Card className="rounded-2xl p-6 text-center border-[#E0F2FE]">
            <CheckCircle2 className="mx-auto text-[#34D399]" size={28} />
            <p className="text-sm text-[#94A3B8] mt-2">Tudo em dia! Nenhuma atividade pendente.</p>
          </Card>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {s?.pending_activities.map((a) => (
            <Card key={a.id} data-testid={`home-activity-${a.id}`} onClick={() => nav("/atividades")}
              className="rounded-2xl p-4 border-[#E0F2FE] card-hover cursor-pointer">
              <Badge variant="secondary" className="mb-2 text-[11px] capitalize">{a.type}</Badge>
              <div className="font-semibold text-[#1F3B4D]">{a.title}</div>
              <p className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{a.instructions}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Grid of quick access */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card data-testid="next-session-card" className="rounded-2xl p-5 border-[#E0F2FE] card-hover">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#60A5FA]">
            <CalendarClock size={15} /> Próxima sessão
          </div>
          {s?.next_session ? (
            <>
              <div className="mt-2 font-semibold text-[#1F3B4D] capitalize">{fmtDate(s.next_session.scheduled_at)}</div>
              <div className="text-sm text-[#94A3B8]">{s.next_session.location || "A definir"} · {s.next_session.duration} min</div>
              <Button variant="outline" onClick={() => nav("/agenda")} className="mt-3 rounded-full border-[#E0F2FE]" size="sm" data-testid="go-agenda-button">Ver agenda</Button>
            </>
          ) : <p className="text-sm text-[#94A3B8] mt-2">Nenhuma sessão agendada.</p>}
        </Card>

        <Card data-testid="quick-record-card" className="rounded-2xl p-5 border-[#E0F2FE] card-hover">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0F766E]">
            <NotebookPen size={15} /> Meu registro
          </div>
          <p className="text-sm text-[#94A3B8] mt-2">Registre rapidamente algo importante — por texto ou áudio.</p>
          <Button onClick={() => nav("/registro")} className="mt-3 rounded-full bg-[#0F766E] hover:bg-[#0d6259]" size="sm" data-testid="go-record-button">Registrar</Button>
        </Card>

        <Card className="rounded-2xl p-5 border-[#E0F2FE] card-hover" data-testid="for-you-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#34D399]">
            <BookOpen size={15} /> Para você
          </div>
          <p className="text-sm text-[#94A3B8] mt-2">{s?.materials?.length || 0} conteúdo(s) indicado(s) pelo seu profissional.</p>
          <Button variant="outline" onClick={() => nav("/conteudos")} className="mt-3 rounded-full border-[#E0F2FE]" size="sm" data-testid="go-materials-button">Ver conteúdos</Button>
        </Card>

        <Card className="rounded-2xl p-5 border-[#E0F2FE] card-hover" data-testid="talk-professional-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#60A5FA]">
            <MessageCircle size={15} /> Falar com meu profissional
          </div>
          <p className="text-sm text-[#94A3B8] mt-2">
            {s?.professional ? `${professionalLabel(s.professional.professional_type)}: ${s.professional.name}` : "Seu profissional"}
            {s?.unread_messages > 0 && <span className="ml-2 text-[#0F766E] font-semibold">{s.unread_messages} nova(s)</span>}
          </p>
          <Button variant="outline" onClick={() => nav("/mensagens")} className="mt-3 rounded-full border-[#E0F2FE]" size="sm" data-testid="go-messages-button">Enviar mensagem</Button>
        </Card>
      </div>

      {/* Preciso de apoio */}
      <Card onClick={() => nav("/apoio")} data-testid="pedir-ajuda-home-card"
        className="rounded-2xl p-5 border-0 cursor-pointer card-hover"
        style={{ background: "linear-gradient(90deg,rgba(239,68,68,.14),rgba(255,255,255,1),rgba(245,158,11,.12))" }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#EF4444] grid place-items-center text-white shrink-0"><LifeBuoy size={22} /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#1F3B4D]">Pedir ajuda</div>
            <p className="text-sm text-[#1F3B4D]/70">Estratégias do seu plano, seu conselheiro e sua rede de apoio — a poucos toques.</p>
          </div>
          <ArrowRight className="text-[#EF4444]" />
        </div>
      </Card>
    </div>
  );
}
